import React, { useContext, useState } from "react";
import { AuthContext } from "../contexts/authContext";
import { LetterboxdCSVData, LetterboxdData } from "../models/letterboxd-data";
import Papa from "papaparse";
import imgTMDB from "../assets/tmdb.svg";
import imgLetterboxd from "../assets/letterboxd.svg";
import DragNDropInput from "../components/drag-n-drop/drag-n-drop-input";
import fileIcon from "../assets/file.svg";
import trashIcon from "../assets/trash.svg";
import { Info } from 'lucide-react';

import "./import.css";
import { useNavigate } from "react-router-dom";
import LetterboxdTutorialPopover from "../components/letterboxd-tutorial-popover";

type PageState = "default" | "importing" | "success" | "error" | "partial_error";
type ImportSource = "letterboxd" | "tmdb" | null;

export default function Import() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [lbData, setLbData] = useState<LetterboxdData[]>();
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [pagestate, setPagestate] = useState<PageState>("default");
  const [importSource, setImportSource] = useState<ImportSource>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorDetails, setErrorDetails] = useState<string>("");
  const [failedImports, setFailedImports] = useState<LetterboxdData[]>([]);
  const [importedCount, setImportedCount] = useState<number>(0);

  function deleteFile(): void {
    setLbData(undefined);
    setUploadedFile(undefined);
  }

  function parseCSVintoLetterboxdData(csv: string): LetterboxdData[] {
    const parsed = Papa.parse<LetterboxdCSVData>(csv, {
      header: true,
      skipEmptyLines: true,
    });
    return parsed.data.map((row) => {
      return { title: row.Name, year: parseInt(row.Year) || 0 };
    });
  }

  async function readFile(files: File[]) {
    setUploadedFile(files[0]);
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const fileContent = e.target?.result;
      if (fileContent && typeof fileContent === "string") {
        const letterboxData = parseCSVintoLetterboxdData(fileContent);
        setLbData(letterboxData);
      }
    };
    reader.readAsText(files[0]);
  }

  async function uploadLetterboxdData() {
    if (authContext?.authApiClient && lbData) {
      setPagestate("importing");
      setImportSource("letterboxd");
      setImportedCount(lbData.length);
      setErrorMessage("");
      setErrorDetails("");
      
      try {
        const importResult = await authContext?.authApiClient.post(
          "/movies/import/letterboxd",
          lbData,
        );
        console.log(importResult);
        const data = importResult.data;
        switch (data.status) {
          case "SUCCESS":
            setPagestate("success");
            break;
          case "PARTIAL_FAILURE":
            setPagestate("partial_error");
            setFailedImports(data.failedImports)
            break;
          case "FAILURE":
          default:
            setPagestate("error");
            setErrorDetails(data.errorDetails)
            break;
        }
      } catch (e: any) {
        setPagestate("error");
        console.error("Error importing movies", e);
        if (e.response?.data?.errorDetails) {
          setErrorDetails(e.response.data.details);
        }
      }
    }
  }

  async function importTMDBTop100() {
    if (authContext?.authApiClient) {
      setPagestate("importing");
      setImportSource("tmdb");
      setImportedCount(100);
      setErrorMessage("");
      setErrorDetails("");
      
      try {
        await authContext?.authApiClient.post("/movies/import/tmdb");
        setPagestate("success");
      } catch (e: any) {
        setPagestate("error");
        console.error("Error uploading data", e);
        
        // Mock error handling
        if (e.response?.data?.message) {
          setErrorMessage(e.response.data.message);
        } else if (e.response?.status === 429) {
          setErrorMessage("Rate limit exceeded");
          setErrorDetails("TMDB API rate limit reached. Please wait a few minutes before trying again.");
        } else if (e.response?.status === 503) {
          setErrorMessage("TMDB service unavailable");
          setErrorDetails("The TMDB service is temporarily unavailable. Please try again later.");
        } else {
          setErrorMessage("Import failed");
          setErrorDetails("An unexpected error occurred. Please try again later.");
        }
        
        if (e.response?.data?.details) {
          setErrorDetails(e.response.data.details);
        }
      }
    }
  }

  function handleRetry() {
    setPagestate("default");
    setErrorMessage("");
    setErrorDetails("");
    setImportSource(null);
    setLbData(undefined);
    setUploadedFile(undefined);
  }

  function handleGoToVote() {
    navigate("/vote");
  }

  function LoadingScreen() {
    const sourceName = importSource === "letterboxd" ? "Letterboxd" : "TMDB";
    
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <svg className="animate-spin h-16 w-16 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Importing your movies...</h2>
          <p className="text-gray-300">
            Processing {importedCount} movies from {sourceName}
          </p>
          <p className="text-sm text-gray-100 mt-2">
            This may take a minute. Please don't close this page.
          </p>
        </div>
      </div>
    );
  }

  function SuccessScreen() {
    const sourceName = importSource === "letterboxd" ? "Letterboxd" : "TMDB";
    
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <svg className="w-20 h-20 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-2 text-green-600">Import Successful!</h2>
          <p className="text-gray-300 text-lg mb-1">
            {importedCount} movies imported from {sourceName}
          </p>
          <p className="text-gray-200 text-sm">
            Your movie collection has been updated
          </p>
        </div>
        <button
          onClick={handleGoToVote}
          className="btn-primary px-8 py-3 mt-4"
        >
          START VOTING
        </button>
      </div>
    );
  }

  function ErrorScreen() {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 max-w-2xl mx-auto">
        <svg className="w-20 h-20 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-2 text-red-600">Import Failed</h2>
          <p className="text-gray-300 text-lg mb-2">{errorMessage || "Something went wrong"}</p>
          {errorDetails && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 text-left max-w-lg">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm text-red-800 font-medium mb-1">Error Details:</p>
                  <p className="text-sm text-red-700">{errorDetails}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <button
          onClick={handleRetry}
          className="btn-primary px-8 py-3 mt-4"
        >
          TRY AGAIN
        </button>
      </div>
    );
  }
  function PartialErrorScreen() {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 max-w-2xl mx-auto">
        <Info className="size-12" color="#eab308"/>
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-2 text-green-600">Import Successful!</h2>
          <h4 className="text-xl font-bold mb-2 text-yellow-500">But some movies were not found</h4>
          <h4 className="text-left font-bold mb-2 text-yellow-500">Here's the list of movies that failed to import:</h4>
          {failedImports && (
            <ul className="text-left list-disc max-h-[50vh] overflow-y-scroll">
              {failedImports.map((movieData, index) => (
                <li className="text-xl" key={index}>{movieData.title} ({movieData.year})</li>
                ))}
            </ul>
          )}
        </div>
        <button
          onClick={handleGoToVote}
          className="btn-primary px-8 py-3 mt-4"
        >
          START VOTING
        </button>
      </div>
    );
  }

  function LetterboxdUploader() {
    return (
      <div className="flex flex-col items-center gap-y-10">
        <div className="flex flex-row relative">
          <h2 className="text-2xl font-bold mr-1">Letterboxd Profile</h2>
          <LetterboxdTutorialPopover />
        </div>
        <div>
          <img alt="Letterboxd logo" src={imgLetterboxd} width={200} />
        </div>
        {lbData ? (
          <div className="w-full max-w-md">
            <div className="file-list w-full bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row items-center gap-2">
                  <img src={fileIcon} alt="file" className="w-5 h-5" />
                  <span className="text-sm font-medium">{uploadedFile?.name}</span>
                </div>
                <img
                  className="cursor-pointer w-5 h-5 hover:opacity-70 transition-opacity"
                  onClick={deleteFile}
                  src={trashIcon}
                  alt="delete"
                />
              </div>
            </div>
            <div className="text-center mt-3 text-green-600 font-medium">
              ✓ Found {lbData.length} movies ready for import
            </div>
          </div>
        ) : (
          <DragNDropInput onFilesSelected={readFile} />
        )}
        <div>
          <button
            onClick={uploadLetterboxdData}
            className={"btn-primary px-8 py-3 transition-all " + ((lbData && lbData?.length > 0) ? "hover:scale-105" : "")}
            disabled={!lbData}
          >
            IMPORT MOVIES
          </button>
        </div>
      </div>
    );
  }

  function TMDBUploader() {
    return (
      <div className="flex flex-col items-center gap-y-10">
        <div>
          <h2 className="text-2xl font-bold">TMDB Top 100</h2>
        </div>
        <div>
          <img alt="TMDB logo" src={imgTMDB} width={150} />
        </div>
        <div className="text-center text-gray-200">
          Add the top 100 movies according to
          <br /> TMDB users to your collection
        </div>
        <div>
          <button onClick={importTMDBTop100} className="btn-primary px-8 py-3 transition-all hover:scale-105">
            IMPORT MOVIES
          </button>
        </div>
      </div>
    );
  }

  function MainContent() {
    return (
      <div>
        <div className="mx-auto w-fit mb-12">
          <h1 className="font-bold text-3xl">Select Your Movie Source</h1>
        </div>
        <div className="flex flex-auto flex-col sm:flex-row justify-center align-items-center gap-10">
          <TMDBUploader />
          <div className="w-fill sm:w-0 border-b-2 sm:border-r-2 border-orange-200 sm:h-96"></div>
          <LetterboxdUploader />
        </div>
      </div>
    );
  }

  function ContentSwitcher() {
    switch (pagestate) {
      case "importing":
        return <LoadingScreen />;
      case "success":
        return <SuccessScreen />;
      case "error":
        return <ErrorScreen />;
      case "partial_error":
        return <PartialErrorScreen />;
      default:
        return <MainContent />;
    }
  }

  return (
    <main className="container mx-auto mt-20 flex flex-col gap-y-20 px-4 mb-10">
      <ContentSwitcher />
    </main>
  );
}